import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import PropTypes from 'prop-types';
import { MuiOtpInput } from 'mui-one-time-password-input';

import { Modal, Divider, Button, Checkbox, Text, Progress, Box, Stack, Anchor, Group, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { enqueueSnackbar } from 'notistack';
import callAzureFunction from 'utils/callAzureFunctions';
import PhoneInput from 'ui-components/forms/PhoneInput';
import { useNavigate } from 'react-router-dom';
import { setUserData } from 'store/features/userSlice';

import { coral } from 'theme/colors';

const RESEND_COOLDOWN_SECONDS = 30;

const OTPDialog = ({ open, setOpen, title = 'Verify your OTP to access the feature', navigateOnClose, onVerified }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state.user?.data);

  const { userEmail } = userData || {};
  const [isLoading, setIsLoading] = useState(false);

  const [mobileNumber, setMobileNumber] = useState('');
  const [formattedMobileNumber, setFormattedMobileNumber] = useState(null);
  const [otp, setOtp] = useState('');
  const [isOTPStage, setIsOTPStage] = useState(false);
  const [isConsentGranted, setIsConsentGranted] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const isSMscreenandDown = useMediaQuery('(max-width: 37.5em)');

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setMobileNumber('');
      setFormattedMobileNumber(null);
      setOtp('');
      setIsOTPStage(false);
      setIsConsentGranted(false);
      setResendCooldown(0);
    }
  }, [open]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChangeOtp = (newValue) => {
    setOtp(newValue);
  };

  const handleSubmitMobileNumber = async () => {
    try {
      setIsLoading(true);

      await callAzureFunction({
        url: 'user/send-otp',
        method: 'post',
        data: { phone_number: formattedMobileNumber, email: userEmail }
      });

      // Update Redux with the new number immediately, marked as unverified
      dispatch(setUserData({ data: { mobileNumber: formattedMobileNumber, isMobileVerified: false } }));

      setIsOTPStage(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      enqueueSnackbar({
        variant: 'info',
        message: 'We have sent the OTP to your phone number.',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } catch (err) {
      enqueueSnackbar({
        variant: 'error',
        message: 'Please try using another phone number or try again later',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsLoading(true);

      await callAzureFunction({
        url: 'user/send-otp',
        method: 'post',
        data: { phone_number: formattedMobileNumber, email: userEmail }
      });

      setOtp('');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      enqueueSnackbar({
        variant: 'info',
        message: 'OTP has been resent to your phone number.',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } catch (err) {
      enqueueSnackbar({
        variant: 'error',
        message: 'Failed to resend OTP. Please try again later.',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitOTP = async () => {
    try {
      const response = await callAzureFunction({
        url: 'user/verify-otp',
        method: 'post',
        data: { otp, email: userEmail }
      });

      const { isOTPVerified } = response?.data || {};

      if (isOTPVerified) {
        enqueueSnackbar({
          variant: 'success',
          message: 'OTP verified successfully.',
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
        dispatch(setUserData({ data: { isOTPVerified: true, mobileNumber: formattedMobileNumber, isMobileVerified: true } }));

        if (onVerified) {
          onVerified(formattedMobileNumber);
        }

        setOpen(false);
      }
    } catch (err) {
      enqueueSnackbar({
        variant: 'error',
        message: 'OTP failed. Please try again',
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (navigateOnClose) {
      navigate(navigateOnClose);
    }
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      size="sm"
      title={
        <Title order={3} c={coral[6]}>
          {title}
        </Title>
      }
    >
      <Progress size="xs" animated={isLoading} value={isLoading ? 100 : 0} />
      <Box>
        {isOTPStage ? (
          <>
            <Title order={4} py={4} mb="md">
              Enter your OTP
            </Title>
            <Divider color={coral[6]} />
            <Box
              style={{
                width: '75%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '0 auto'
              }}
            >
              <MuiOtpInput
                value={otp}
                onChange={handleChangeOtp}
                length={6}
                my={3}
                autoFocus
                gap={isSMscreenandDown ? 1 : 2}
                sx={isSMscreenandDown ? { px: 0 } : { px: 2 }}
                TextFieldsProps={{
                  variant: 'standard',
                  sx: { py: 2, maxWidth: 75 },
                  type: 'tel'
                }}
                validateChar={(char) => /^[0-9]$/.test(char)}
              />
            </Box>
            <Group justify="space-between" align="center">
              <Anchor size="sm" c={coral[6]} style={{ cursor: 'pointer' }} onClick={() => setIsOTPStage(false)}>
                Use another mobile number instead
              </Anchor>
              <Button size="xs" variant="subtle" onClick={handleResendOTP} disabled={resendCooldown > 0 || isLoading}>
                {resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : 'Resend OTP'}
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Title order={4} py={4}>
              Enter your phone number
            </Title>
            <PhoneInput
              id="outlined-adornment-mobile-phone"
              label="Mobile Number"
              defaultCountry="US"
              value={mobileNumber}
              onChange={(phone) => {
                setMobileNumber(phone);
              }}
              handleFormattedValue={setFormattedMobileNumber}
            />

            <Checkbox
              checked={isConsentGranted}
              onChange={(e) => setIsConsentGranted(e.currentTarget.checked)}
              label="By providing your phone number, you consent to receive text message communications from Drip Tours."
              my="sm"
            />
          </>
        )}
      </Box>
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={handleClose}>
          Cancel
        </Button>
        {isOTPStage ? (
          <Button onClick={handleSubmitOTP} color="coral.6" autoFocus disabled={!otp}>
            Confirm OTP
          </Button>
        ) : (
          <Button onClick={handleSubmitMobileNumber} color="coral.6" autoFocus disabled={!isConsentGranted || !formattedMobileNumber}>
            Submit
          </Button>
        )}
      </Group>
    </Modal>
  );
};

OTPDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  title: PropTypes.string,
  navigateOnClose: PropTypes.string,
  onVerified: PropTypes.func
};

export default OTPDialog;
